import {
  getAccount,
  getCostCodes,
  getCostTypes,
  getPersistedAgaveAccountToken,
  searchGlCodes,
  searchProjects,
  searchVendors,
  selectAgaveSlice,
} from 'app/pages/store/agaveSlice';
import { useAppDispatch, useAppSelector } from 'app/store';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo } from 'react';

export const useFetchAgaveCommonData = () => {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { selectedPaidol } = useAppSelector(selectUserCompanies);
  const { accountToken } = useAppSelector(selectAgaveSlice);

  const agaveAccountToken = useMemo(
    () => selectedPaidol?.agaveAccountToken ?? '',
    [selectedPaidol?.agaveAccountToken]
  );

  const isConstructionType = useMemo(
    () => selectedPaidol?.isConstructionType ?? false,
    [selectedPaidol?.isConstructionType]
  );

  // Strict: this hook fetches via Agave APIs (using agaveAccountToken), so it must NOT
  // run for ERP Connection paidols — they have no agaveAccountToken and their coding data
  // is populated by the QBO sync task (SEE-681/682) into the same Agave* tables.
  const isAgaveClient = useMemo(
    () => selectedPaidol?.isAgaveClient ?? false,
    [selectedPaidol?.isAgaveClient]
  );

  const loadProjects = useCallback(
    (account_token: string) => {
      // HOTFIX 2025-06-04 @graham: hot fix BUT why are we even using getProjects if we do searchProjects?
      return dispatch(
        searchProjects({
          input: {
            filter: {
              paidolId: selectedPaidol?.id ?? '',
            },
          },
        })
      ).unwrap();
    },
    [dispatch, selectedPaidol?.id]
  );

  const loadGlCodes = useCallback(
    (account_token: string) => {
      const input = {
        filter: {
          paidolId: selectedPaidol?.id ?? '',
        },
        limit: 25,
      };
      return dispatch(
        searchGlCodes({
          input,
        })
      ).unwrap();
    },
    [dispatch, selectedPaidol?.id]
  );

  const loadCostCodes = useCallback(
    (account_token: string) => {
      // HOTFIX 2025-06-04 @graham: temporary fix as otherwise only returns 100 cost codes and afaict
      // opensearch is not used for cost codes. currently users not able to select all cost codes in agave.
      return dispatch(getCostCodes({ paidolId: selectedPaidol?.id ?? '', limit: 1000 })).unwrap();
    },
    [dispatch, selectedPaidol?.id]
  );

  const loadCostTypes = useCallback(
    (account_token: string) => {
      // HOTFIX 2025-06-04 @graham: remove once hotfix is 'fixed'
      return dispatch(getCostTypes({ paidolId: selectedPaidol?.id ?? '', limit: 1000 })).unwrap();
    },
    [dispatch, selectedPaidol?.id]
  );

  const loadVendors = useCallback(
    (account_token: string) => {
      return dispatch(
        searchVendors({
          input: {
            filter: {
              paidolId: selectedPaidol?.id ?? '',
            },
          },
        })
      ).unwrap();
    },
    [dispatch, selectedPaidol?.id]
  );

  const getAgaveAccount = useCallback(
    (account_token: string) => {
      return dispatch(getAccount({ input: { account_token } })).unwrap();
    },
    [dispatch]
  );

  useEffect(() => {
    if (isConstructionType && isAgaveClient && selectedPaidol?.id && !accountToken) {
      dispatch(getPersistedAgaveAccountToken(selectedPaidol.id));
    }
  }, [accountToken, agaveAccountToken, dispatch, isAgaveClient, isConstructionType, selectedPaidol?.id]);

  useEffect(() => {
    if (isConstructionType && isAgaveClient && agaveAccountToken) {
      // loadProjects(agaveAccountToken); // Commented out - let dropdowns handle their own pagination

      // Fetch data with error handling to prevent failures from breaking the app
      loadGlCodes(agaveAccountToken).catch((err) => {
        console.warn('[useFetchAgaveCommonData] Failed to load GL codes:', err);
      });

      getAgaveAccount(agaveAccountToken).catch((err) => {
        console.warn('[useFetchAgaveCommonData] Failed to load Agave account:', err);
        enqueueSnackbar(
          'ERP connection unavailable — please ensure your Web Connector is running and refresh',
          {
            variant: 'warning',
          }
        );
      });

      loadCostCodes(agaveAccountToken).catch((err) => {
        console.warn('[useFetchAgaveCommonData] Failed to load cost codes:', err);
      });

      loadCostTypes(agaveAccountToken).catch((err) => {
        console.warn('[useFetchAgaveCommonData] Failed to load cost types:', err);
      });

      loadVendors(agaveAccountToken).catch((err) => {
        console.warn('[useFetchAgaveCommonData] Failed to load vendors:', err);
      });
    }
  }, [
    agaveAccountToken,
    isConstructionType,
    isAgaveClient,
    getAgaveAccount,
    dispatch,
    loadCostCodes,
    loadCostTypes,
    loadGlCodes,
    // loadProjects, // Commented out
    loadVendors,
  ]);
};
